export enum RbacRoleGremlinInternalCompany {
  ADMIN = 'ADMIN',
  DEV = 'DEV',
  BOOTCAMP_ORGANIZER = 'BOOTCAMP_ORGANIZER'
}

export enum RbacRoleCompany {
  COMPANY_ADMIN = 'COMPANY_ADMIN',
  COMPANY_COORDINATOR = 'COMPANY_COORDINATOR',
  COMPANY_MANAGER = 'COMPANY_MANAGER',
  COMPANY_OWNER = 'COMPANY_OWNER',
  COMPANY_USER = 'COMPANY_USER',
  DEFAULT = 'DEFAULT' // this is an alias on the BE, but this is a value we will receive in the response and is a valid value we can send in the request - see libraries/core/src/main/java/com/gremlininc/auth/models/Role.java
}

export enum RbacRoleTeam {
  TEAM_CREDENTIAL_MANAGER = 'TEAM_CREDENTIAL_MANAGER',
  TEAM_MANAGER = 'TEAM_MANAGER',
  TEAM_USER = 'TEAM_USER',
  TEAM_VIEWER = 'TEAM_VIEWER',
  DEFAULT = 'DEFAULT' // this is an alias on the BE, but this is a value we will receive in the response and is a valid value we can send in the request - see libraries/core/src/main/java/com/gremlininc/auth/models/Role.java
}

export type RbacRolesAll = string // this has the type of RbacRoleGremlinInternalCompany | RbacRoleCompany | RbacRoleTeam - or string when it is a custom role

export enum RbacPrivilege {
  // Company
  ACCESS_SECURITY_LOGS = 'ACCESS_SECURITY_LOGS',
  ACTIVATE_USER = 'ACTIVATE_USER',
  ADD_USER_TO_COMPANY = 'ADD_USER_TO_COMPANY',
  ADD_USER_TO_COMPANY_WITHOUT_INVITE = 'ADD_USER_TO_COMPANY_WITHOUT_INVITE',
  COMPANY_DEFAULT = 'COMPANY_DEFAULT',
  CREATE_REMOVE_TEAM = 'CREATE_REMOVE_TEAM',
  DEACTIVATE_USER = 'DEACTIVATE_USER',
  MANAGE_COMPANY_PREFS = 'MANAGE_COMPANY_PREFS',
  MANAGE_COMPANY_USER_PERMISSIONS = 'MANAGE_COMPANY_USER_PERMISSIONS',
  MANAGE_INTEGRATIONS = 'MANAGE_INTEGRATIONS',
  MANAGE_MFA = 'MANAGE_MFA',
  MANAGE_OAUTH = 'MANAGE_OAUTH',
  MANAGE_SAML = 'MANAGE_SAML',
  MANAGE_TEST_SUITES = 'MANAGE_TEST_SUITES',
  PROMOTE_DEMOTE_OWNER = 'PROMOTE_DEMOTE_OWNER',
  READ_ALL_API_KEYS = 'READ_ALL_API_KEYS',
  READ_ALL_CLIENTS = 'READ_ALL_CLIENTS',
  READ_ALL_INTEGRATION_CLIENTS = 'READ_ALL_INTEGRATION_CLIENTS',
  READ_ALL_USERS = 'READ_ALL_USERS',
  READ_COMPANY_RM_REPORTS = 'READ_COMPANY_RM_REPORTS',
  UNSHARE_SCENARIO = 'UNSHARE_SCENARIO',

  // Team
  ACTIVATE_API_KEY = 'ACTIVATE_API_KEY',
  ACTIVATE_CLIENT = 'ACTIVATE_CLIENT',
  ADD_REMOVE_USER_FROM_TEAM = 'ADD_REMOVE_USER_FROM_TEAM',
  CREATE_SCENARIO = 'CREATE_SCENARIO',
  EDIT_SCENARIO = 'EDIT_SCENARIO',
  MANAGE_MONITORING_FOR_TEAM = 'MANAGE_MONITORING_FOR_TEAM',
  MANAGE_NOTIFICATION_SETTINGS_FOR_TEAM = 'MANAGE_NOTIFICATION_SETTINGS_FOR_TEAM',
  MANAGE_TEAM_CREDENTIALS = 'MANAGE_TEAM_CREDENTIALS',
  MANAGE_TEAM_DETAILS = 'MANAGE_TEAM_DETAILS',
  MANAGE_TEAM_USER_PERMISSIONS = 'MANAGE_TEAM_USER_PERMISSIONS',
  MANAGE_WEBHOOKS = 'MANAGE_WEBHOOKS',
  READ_TEAM_ATTACKS = 'READ_TEAM_ATTACKS',
  READ_TEAM_CLIENTS = 'READ_TEAM_CLIENTS',
  READ_TEAM_SCENARIOS = 'READ_TEAM_SCENARIOS',
  READ_TEAM_SCHEDULES = 'READ_TEAM_SCHEDULES',
  READ_TEAM_USERS = 'READ_TEAM_USERS',
  RUN_TEAM_ADHOC_ATTACKS = 'RUN_TEAM_ADHOC_ATTACKS',
  RUN_TEAM_SCENARIO_ATTACKS = 'RUN_TEAM_SCENARIO_ATTACKS',
  SHARE_ASSET = 'SHARE_ASSET',
  SHARE_SCENARIO = 'SHARE_SCENARIO',
  TEAM_DEFAULT = 'TEAM_DEFAULT',

  // EN-6460 Migrated Values to replace above privileges
  ALL_API_KEYS_READ = 'ALL_API_KEYS_READ', // company level
  API_KEYS_READ = 'API_KEYS_READ',
  API_KEYS_WRITE = 'API_KEYS_WRITE',
  CLIENTS_READ = 'CLIENTS_READ',
  CLIENTS_WRITE = 'CLIENTS_WRITE',
  COMPANIES_READ = 'COMPANIES_READ',
  COMPANIES_WRITE = 'COMPANIES_WRITE',
  COMPANY_PREFERENCES_WRITE = 'COMPANY_PREFERENCES_WRITE',
  COMPANY_SECURITY_WRITE = 'COMPANY_SECURITY_WRITE',
  COMPANY_USERS_READ = 'COMPANY_USERS_READ',
  COMPANY_USERS_WRITE = 'COMPANY_USERS_WRITE',
  EXPERIMENTS_READ = 'EXPERIMENTS_READ',
  EXPERIMENTS_RUN = 'EXPERIMENTS_RUN',
  EXPERIMENTS_WRITE = 'EXPERIMENTS_WRITE',
  HALT_ALL = 'HALT_ALL',
  HALT_WRITE = 'HALT_WRITE',
  IMAGES_READ = 'IMAGES_READ',
  IMAGES_WRITE = 'IMAGES_WRITE',
  INTEGRATIONS_READ = 'INTEGRATIONS_READ',
  INTEGRATIONS_WRITE = 'INTEGRATIONS_WRITE',
  RELIABILITY_MANAGEMENT_READ = 'RELIABILITY_MANAGEMENT_READ',
  RELIABILITY_MANAGEMENT_RUN = 'RELIABILITY_MANAGEMENT_RUN',
  RELIABILITY_REPORTS_READ = 'RELIABILITY_REPORTS_READ',
  REPORTS_READ = 'REPORTS_READ',
  ROLES_WRITE = 'ROLES_WRITE',
  SCENARIO_SHARE_WRITE = 'SCENARIO_SHARE_WRITE',
  SCENARIOS_READ = 'SCENARIOS_READ',
  SCENARIOS_RUN = 'SCENARIOS_RUN',
  SCENARIOS_WRITE = 'SCENARIOS_WRITE',
  SCHEDULES_READ = 'SCHEDULES_READ',
  SCHEDULES_WRITE = 'SCHEDULES_WRITE',
  SECURITY_REPORTS_READ = 'SECURITY_REPORTS_READ',
  SERVICES_READ = 'SERVICES_READ',
  SERVICES_WRITE = 'SERVICES_WRITE',
  TEAM_SECURITY_READ = 'TEAM_SECURITY_READ',
  TEAM_SECURITY_WRITE = 'TEAM_SECURITY_WRITE',
  TEAMS_READ = 'TEAMS_READ',
  TEAMS_WRITE = 'TEAMS_WRITE',
  TEST_SUITES_READ = 'TEST_SUITES_READ',
  TEST_SUITES_WRITE = 'TEST_SUITES_WRITE',
  USERS_READ = 'USERS_READ',
  USERS_WRITE = 'USERS_WRITE',
  WEBHOOKS_READ = 'WEBHOOKS_READ',
  WEBHOOKS_WRITE = 'WEBHOOKS_WRITE',

  // Internal privileges
  ADMIN = 'ADMIN',
  DEV = 'DEV',
  MANAGE_BOOTCAMPS = 'MANAGE_BOOTCAMPS'
}

export interface RbacRoleItem {
  description?: string
  privileges: RbacPrivilege[]
}

export enum RbacRoleItemType {
  COMPANY = 'Company',
  TEAM = 'Team'
}

export interface RbacRoleCompanyItem extends RbacRoleItem {
  key: RbacRoleCompany | RbacRoleGremlinInternalCompany
  name: RbacRoleCompany | RbacRoleGremlinInternalCompany
  type: RbacRoleItemType.COMPANY
}

export interface RbacCustomRoleCompanyItem extends RbacRoleItem {
  name: string
  key: string
  type: RbacRoleItemType.COMPANY
}

export interface RbacRoleTeamItem extends RbacRoleItem {
  key: RbacRoleTeam
  name: RbacRoleTeam
  type: RbacRoleItemType.TEAM
}

export interface RbacCustomRoleTeamItem extends RbacRoleItem {
  key: string
  name: string
  type: RbacRoleItemType.TEAM
}

export enum RbacPrivilegeItemCategory {
  ADMINISTRATIVE = 'ADMINISTRATIVE',
  ALL_API_KEYS = 'ALL_API_KEYS',
  API_KEYS = 'API_KEYS',
  CLIENTS = 'CLIENTS',
  COMPANIES = 'COMPANIES',
  COMPANY_PREFERENCES = 'COMPANY_PREFERENCES',
  COMPANY_SECURITY = 'COMPANY_SECURITY',
  COMPANY_USERS = 'COMPANY_USERS',
  EXPERIMENTS = 'EXPERIMENTS',
  HALT = 'HALT',
  HALT_ALL = 'HALT_ALL',
  IMAGES = 'IMAGES',
  INTEGRATIONS = 'INTEGRATIONS',
  RELIABILITY_MANAGEMENT = 'RELIABILITY_MANAGEMENT',
  RELIABILITY_REPORTS = 'RELIABILITY_REPORTS',
  REPORTS = 'REPORTS',
  REQUIRED = 'REQUIRED',
  SCENARIOS = 'SCENARIOS',
  SCENARIO_SHARE = 'SCENARIO_SHARE',
  SECURITY_REPORTS = 'SECURITY_REPORTS',
  SERVICES = 'SERVICES',
  TEAMS = 'TEAMS',
  TEAM_SECURITY = 'TEAM_SECURITY',
  TEST_SUITES = 'TEST_SUITES',
  USERS = 'USERS',
  WEBHOOKS = 'WEBHOOKS'
}

export interface RbacPrivilegeItem {
  key: RbacPrivilege
  description: string
  category: RbacPrivilegeItemCategory
}

export type CompanyRoles = Partial<
  Record<RbacRoleCompany | RbacRoleGremlinInternalCompany, RbacRoleCompanyItem>
>

export type CustomCompanyRoles = Record<string, RbacCustomRoleCompanyItem>

export type TeamRoles = Record<RbacRoleTeam, RbacRoleTeamItem>

export type CustomTeamRoles = Record<string, RbacCustomRoleTeamItem>

export interface RbacBody {
  roles: {
    company: CompanyRoles
    team: TeamRoles
  }
  privileges: {
    company: Partial<Record<RbacPrivilege, RbacPrivilegeItem>>
    team: Partial<Record<RbacPrivilege, RbacPrivilegeItem>>
  }
  customRoles: {
    company: CustomCompanyRoles
    team: CustomTeamRoles
  }
}
